import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart,
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import {
  getAffiliatesPeriod,
  getAffiliatesCampaignChart,
  getAffiliatesSyncMeta,
  getAffiliatesTimeseries,
  runAffiliatesSync,
  type AffiliatePeriodPartner,
  type AffiliatesPeriodResult,
  type AffiliatesGranularity,
  type AffiliatesTimeseriesPoint,
  type CampaignChartResult,
} from '../api/rondo';
import { useTabParam } from '../shell/useTabParam';
import { ServiceBar } from '../shell/ServiceBar';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { Dialog, DialogContent } from '../components/arc/dialog/dialog';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import './finance-affiliates.css';

Chart.register(
  BarController, LineController,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, Filler,
);

type Tab = 'zakladni' | 'vypis';
const FIN_AFF_TABS: readonly Tab[] = ['zakladni', 'vypis'];
type Currency = 'EUR' | 'CZK';
type ChartMetric = 'clicks' | 'registrations' | 'ftd_count' | 'deposits_count' | 'deposits_volume' | 'ngr_volume' | 'commission';
type ChartRange = '3' | '6' | '12' | 'all';
type ChartGranularity = 'day' | 'month';

const COUNTRY_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const MONTHS_CS = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',
];

const NUMBER_FMT = new Intl.NumberFormat('cs-CZ');
const fmtInt = (v: number): string => NUMBER_FMT.format(Math.round(Number(v) || 0));
const fmtMoney = (v: number, currency: Currency, rate: number): string => {
  const n = Number(v) || 0;
  if (currency === 'CZK') return `${Math.round(n * rate).toLocaleString('cs-CZ')} Kč`;
  return `${Math.round(n).toLocaleString('cs-CZ')} €`;
};

const AVATAR_PALETTE = [
  '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545', '#fd7e14',
  '#198754', '#20c997', '#0dcaf0', '#0891b2', '#137333', '#7c3aed',
];
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length] as string;
}
function initials(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase();
}

export function FinanceAffiliatesPage() {
  const [tab, setTab] = useTabParam<Tab>('tab', FIN_AFF_TABS, 'zakladni');
  const [drill, setDrill] = useState<{ campaignId: string; campaignName: string } | null>(null);

  return (
    <section className="rondo-finance-affiliates rondo-arc-affiliates p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">FINANCE</div><h1>Affiliates</h1><p>Výkon affiliate partnerů, vklady a provize napříč trhy</p></div></header>
      <ArcTabs label="Affiliates" items={[{ value: 'zakladni', label: 'Základní' }, { value: 'vypis', label: 'Výpis' }]} value={tab} onChange={setTab} className="rondo-affiliates-arc-tabs" />

      {tab === 'zakladni' && <ZakladniTab onDrill={(id, name) => setDrill({ campaignId: id, campaignName: name })} />}
      {tab === 'vypis' && <VypisTab onDrill={(id, name) => setDrill({ campaignId: id, campaignName: name })} />}
      {drill && (
        <CampaignDrillModal campaignId={drill.campaignId} campaignName={drill.campaignName} onClose={() => setDrill(null)} />
      )}
    </section>
  );
}

function useCurrency(): [Currency, (c: Currency) => void] {
  const [c, setC] = useState<Currency>(() => {
    try {
      const v = localStorage.getItem('fa-currency');
      return v === 'CZK' ? 'CZK' : 'EUR';
    } catch { return 'EUR'; }
  });
  const set = (v: Currency) => {
    setC(v);
    try { localStorage.setItem('fa-currency', v); } catch { /* ignore */ }
  };
  return [c, set];
}

function ZakladniTab({ onDrill }: { onDrill: (id: string, name: string) => void }) {
  const [data, setData] = useState<AffiliatesPeriodResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useCurrency();
  const [showRatio, setShowRatio] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAffiliatesPeriod({ period: 'month', compare: false })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || 'Chyba načítání'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tick]);

  useEffect(() => {
    getAffiliatesSyncMeta()
      .then((meta) => {
        const last = meta.lastRunAt ? new Date(meta.lastRunAt).getTime() : 0;
        const ageMin = (Date.now() - last) / 60000;
        if (!last || ageMin > 30) {
          runAffiliatesSync()
            .then(() => setTick((t) => t + 1))
            .catch(() => undefined);
        }
      })
      .catch(() => undefined);
  }, []);

  if (loading && !data) return <PageState title="Načítám affiliate výkon" loading />;
  if (error) return <PageState title="Affiliate výkon se nepodařilo načíst" message={error} />;
  if (!data) return <PageState title="Žádná data" />;

  const eurRate = data.eurRate;
  const tot = data.partners.reduce(
    (acc, p) => {
      acc.clicks += p.current.clicks;
      acc.registrations += p.current.registrations;
      acc.ftd += p.current.ftd;
      acc.depositsVolume += p.current.depositsVolume;
      acc.income += p.current.cpa + p.current.rs;
      return acc;
    },
    { clicks: 0, registrations: 0, ftd: 0, depositsVolume: 0, income: 0 },
  );

  return (
    <div className="finance-affiliate-basic">
      <div className="rondo-arc-affiliates-kpis">
        <MetricCard label="Prokliky" value={tot.clicks} context="Aktuální měsíc" />
        <MetricCard label="Registrace" value={tot.registrations} context="Aktuální měsíc" />
        <MetricCard label="FTD" value={tot.ftd} context="Aktuální měsíc" />
        <MetricCard label="Vklady" value={currency === 'CZK' ? Math.round(tot.depositsVolume * eurRate) : Math.round(tot.depositsVolume)} suffix={currency === 'CZK' ? '\u00a0Kč' : '\u00a0€'} context="Aktuální měsíc" />
        <MetricCard label="Příjmy (CPA + RS)" value={currency === 'CZK' ? Math.round(tot.income * eurRate) : Math.round(tot.income)} suffix={currency === 'CZK' ? '\u00a0Kč' : '\u00a0€'} context="Aktuální měsíc" />
      </div>
      <ServiceBar className="finance-affiliate-toolbar" variant="seg" selects={[]} extra={<div className="finance-affiliate-switches"><CurrencyToggle currency={currency} onChange={setCurrency} /><FinanceAffiliateSwitch label="Srovnání" checked={showRatio} onChange={setShowRatio} /></div>} right={<div className="finance-affiliate-meta"><strong>{data.partners.length} partnerů</strong><span>1 € = {eurRate.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč</span>{data.fromCache && <em title="Data jsou načtena z mezipaměti">Uložená data</em>}</div>} />

      <PartnerTable partners={data.partners} currency={currency} eurRate={eurRate} onDrill={onDrill} groupByCountry showRatio={showRatio} />
    </div>
  );
}

function VypisTab({ onDrill }: { onDrill: (id: string, name: string) => void }) {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(() => {
    try {
      const v = localStorage.getItem('fa-vypis-month');
      if (v && /^\d{4}-\d{2}$/.test(v)) return v;
    } catch { /* ignore */ }
    return defaultMonth;
  });
  const [country, setCountry] = useState<string>(() => {
    try { return localStorage.getItem('fa-vypis-country') || ''; } catch { return ''; }
  });
  const [search, setSearch] = useState('');
  const [currency, setCurrency] = useCurrency();
  const [view, setView] = useState<'partneri' | 'mesice'>('partneri');
  const [data, setData] = useState<AffiliatesPeriodResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAffiliatesPeriod({ period: 'month', compare: false, month })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || 'Chyba načítání'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const setMonthPersist = (m: string) => {
    setMonth(m);
    try { localStorage.setItem('fa-vypis-month', m); } catch { /* ignore */ }
  };
  const setCountryPersist = (c: string) => {
    setCountry(c);
    try { localStorage.setItem('fa-vypis-country', c); } catch { /* ignore */ }
  };
  const reset = () => {
    setMonthPersist(defaultMonth);
    setCountryPersist('');
    setSearch('');
  };

  const monthOptions = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const y = d.getFullYear();
      const m = d.getMonth();
      out.push({ value: `${y}-${String(m + 1).padStart(2, '0')}`, label: `${MONTHS_CS[m]} ${y}` });
      d.setMonth(d.getMonth() - 1);
    }
    return out;
  }, []);

  if (view === 'mesice') {
    return (
      <VypisMesiceView
        view={view}
        onChangeView={setView}
        partners={data?.partners ?? []}
        currency={currency}
        onChangeCurrency={setCurrency}
      />
    );
  }

  if (loading && !data) return <PageState title="Načítám výpis partnerů" loading />;
  if (error) return <PageState title="Výpis partnerů se nepodařilo načíst" message={error} />;
  if (!data) return <PageState title="Žádná data" />;

  const filtered = data.partners.filter((p) => {
    if (country && p.country !== country) return false;
    if (search && !p.campaignName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="finance-affiliate-list">
      <ServiceBar className="finance-affiliate-toolbar" variant="seg" selects={[
        { label: 'Měsíc', value: month, onChange: setMonthPersist, options: monthOptions },
        { label: 'Země', value: country, onChange: setCountryPersist, options: [{ value: '', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ' }, { value: 'SK', label: '🇸🇰 SK' }, { value: 'RO', label: '🇷🇴 RO' }] },
      ]} extra={<><VypisViewToggle view={view} onChange={setView} /><CurrencyToggle currency={currency} onChange={setCurrency} /></>} search={search} onSearch={setSearch} searchPlaceholder="Hledat partnera…" onReset={reset} right={<div className="finance-affiliate-meta"><strong>{filtered.length} partnerů</strong><span>1 € = {data.eurRate.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč</span></div>} />

      <PartnerTable partners={filtered} currency={currency} eurRate={data.eurRate} onDrill={onDrill} />
    </div>
  );
}

function VypisViewToggle({ view, onChange }: { view: 'partneri' | 'mesice'; onChange: (v: 'partneri' | 'mesice') => void }) {
  return (
    <div className="finance-affiliate-view">
      <span>Zobrazení</span>
      <CompactSelect value={view} onChange={(value) => onChange(value as 'partneri' | 'mesice')} ariaLabel="Zobrazení výpisu" options={[{ value: 'partneri', label: 'Partneři' }, { value: 'mesice', label: 'Po měsících' }]} />
    </div>
  );
}

function periodLabel(period: string, g: AffiliatesGranularity): string {
  const [y, m, d] = period.split('-').map(Number);
  if (g === 'month') return `${MONTHS_CS[(m ?? 1) - 1]} ${y}`;
  const dmy = `${d}. ${m}. ${y}`;
  return g === 'week' ? `týden ${dmy}` : dmy;
}

function VypisMesiceView({
  view, onChangeView, partners, currency, onChangeCurrency,
}: {
  view: 'partneri' | 'mesice';
  onChangeView: (v: 'partneri' | 'mesice') => void;
  partners: AffiliatePeriodPartner[];
  currency: Currency;
  onChangeCurrency: (c: Currency) => void;
}) {
  const [granularity, setGranularity] = useState<AffiliatesGranularity>('month');
  // '__all__' = sloučený souhrn přes všechny partnery (default) - jen období + celkové odměny.
  const [campaignId, setCampaignId] = useState<string>('__all__');
  const [res, setRes] = useState<{ points: AffiliatesTimeseriesPoint[]; eurRate: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    setLoading(true);
    getAffiliatesTimeseries(granularity, campaignId === '__all__' ? undefined : campaignId)
      .then((d) => { if (!cancelled) { setRes(d); setError(null); } })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || 'Chyba načítání'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [granularity, campaignId]);

  const rows = [...(res?.points ?? [])].reverse();
  const rate = res?.eurRate ?? 25;
  const money = (v: number) => fmtMoney(v, currency, rate);
  // Odměna = CPA + RS (naše celkové odměny). Součty přes zobrazené období do patičky.
  const reward = (p: AffiliatesTimeseriesPoint) => (p.cpa || 0) + (p.rs || 0);
  const tot = rows.reduce((a, p) => ({
    clicks: a.clicks + (p.clicks || 0), registrations: a.registrations + (p.registrations || 0),
    ftd: a.ftd + (p.ftd || 0), depositsCount: a.depositsCount + (p.depositsCount || 0),
    depositsVolume: a.depositsVolume + (p.depositsVolume || 0), cpa: a.cpa + (p.cpa || 0), rs: a.rs + (p.rs || 0),
  }), { clicks: 0, registrations: 0, ftd: 0, depositsCount: 0, depositsVolume: 0, cpa: 0, rs: 0 });
  const tableRows = rows.map((p) => ({ ...p, label: periodLabel(p.period, granularity), reward: reward(p) }));
  type MonthRow = (typeof tableRows)[number];
  const columns: DataColumn<MonthRow>[] = [
    { key: 'label', label: 'Období', width: 160 },
    { key: 'clicks', label: 'Prokliky', numeric: true, render: (_, p) => fmtInt(p.clicks) },
    { key: 'registrations', label: 'Registrace', numeric: true, render: (_, p) => fmtInt(p.registrations) },
    { key: 'ftd', label: 'FTD', numeric: true, render: (_, p) => fmtInt(p.ftd) },
    { key: 'depositsCount', label: 'Vklady (#)', numeric: true, render: (_, p) => fmtInt(p.depositsCount) },
    { key: 'depositsVolume', label: 'Vklady', numeric: true, render: (_, p) => money(p.depositsVolume) },
    { key: 'cpa', label: 'CPA', numeric: true, render: (_, p) => money(p.cpa) },
    { key: 'rs', label: 'RS', numeric: true, render: (_, p) => money(p.rs) },
    { key: 'reward', label: 'Odměny', numeric: true, render: (_, p) => money(p.reward) },
  ];

  return (
    <div className="finance-affiliate-months">
      <ServiceBar className="finance-affiliate-toolbar" variant="seg" selects={[
        { label: 'Partner', value: campaignId, onChange: setCampaignId, minWidth: 220, options: [{ value: '__all__', label: 'Všichni partneři' }, ...partners.map((p) => ({ value: p.campaignId, label: p.campaignName || p.campaignId }))] },
        { label: 'Granularita', value: granularity, onChange: (v) => setGranularity(v as AffiliatesGranularity), options: [{ value: 'day', label: 'Den' }, { value: 'week', label: 'Týden' }, { value: 'month', label: 'Měsíc' }] },
      ]} extra={<><VypisViewToggle view={view} onChange={onChangeView} /><CurrencyToggle currency={currency} onChange={onChangeCurrency} /></>} right={<div className="finance-affiliate-meta">{loading ? 'Načítám…' : `${rows.length} období`}</div>} />

      <div className="rondo-arc-affiliates-kpis rondo-arc-affiliates-month-kpis">
        <MetricCard label="Prokliky" value={tot.clicks} context={`${rows.length} období`} />
        <MetricCard label="Registrace" value={tot.registrations} context={`${rows.length} období`} />
        <MetricCard label="Odměny" value={currency === 'CZK' ? Math.round((tot.cpa + tot.rs) * rate) : Math.round(tot.cpa + tot.rs)} suffix={currency === 'CZK' ? '\u00a0Kč' : '\u00a0€'} context={`${rows.length} období`} />
      </div>
      <div className="rondo-arc-affiliates-table">
        {error ? <PageState title="Historii se nepodařilo načíst" message={error} /> : loading ? <PageState title="Načítám historii" loading /> : <SortableDataTable rows={tableRows} columns={columns} rowKey="period" caption="Výkon affiliate partnerů po obdobích" emptyMessage="Žádná data." />}
      </div>
    </div>
  );
}

function FinanceAffiliateSwitch({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <label className="finance-affiliate-switch"><input type="checkbox" role="switch" checked={checked} onChange={(event) => onChange(event.target.checked)} /><i aria-hidden="true" /><span>{label}</span></label>;
}

function CurrencyToggle({ currency, onChange }: { currency: Currency; onChange: (c: Currency) => void }) {
  return <FinanceAffiliateSwitch label="Zobrazit v CZK" checked={currency === 'CZK'} onChange={(checked) => onChange(checked ? 'CZK' : 'EUR')} />;
}

const COUNTRY_SORT: Record<string, number> = { CZ: 0, SK: 1, RO: 2 };

function PartnerTable({
  partners, currency, eurRate, onDrill, groupByCountry = false, showRatio = true,
}: {
  partners: AffiliatePeriodPartner[];
  currency: Currency;
  eurRate: number;
  onDrill: (id: string, name: string) => void;
  groupByCountry?: boolean;
  showRatio?: boolean;
}) {
  const sorted = useMemo(() => {
    const arr = [...partners];
    if (groupByCountry) {
      arr.sort((a, b) => {
        const ca = COUNTRY_SORT[a.country ?? ''] ?? 99;
        const cb = COUNTRY_SORT[b.country ?? ''] ?? 99;
        if (ca !== cb) return ca - cb;
        return b.current.depositsVolume - a.current.depositsVolume;
      });
    } else {
      arr.sort((a, b) => b.current.depositsVolume - a.current.depositsVolume);
    }
    return arr;
  }, [partners, groupByCountry]);
  const tableRows = sorted.map((p, i) => ({
    ...p,
    rank: i + 1,
    partner: p.campaignName || p.campaignId,
    location: p.country && COUNTRY_FLAG[p.country] ? `${COUNTRY_FLAG[p.country]} ${p.country}` : '—',
    clicks: p.current.clicks,
    registrations: p.current.registrations,
    ftd: p.current.ftd,
    depositsCount: p.current.depositsCount,
    depositsVolume: p.current.depositsVolume,
    cpa: p.current.cpa,
    rs: p.current.rs,
  }));
  type PartnerRow = (typeof tableRows)[number];
  const columns: DataColumn<PartnerRow>[] = [
    { key: 'rank', label: '#', numeric: true, width: 54 },
    { key: 'partner', label: 'Partner', width: 230, render: (_, p) => <PartnerCell campaignId={p.campaignId} campaignName={p.campaignName} /> },
    { key: 'location', label: 'Lokace', width: 100 },
    { key: 'clicks', label: 'Prokliky', numeric: true, render: (_, p) => <Muted v={p.clicks} fmt={fmtInt} /> },
    { key: 'registrations', label: 'Registrace', numeric: true, render: (_, p) => <><Muted v={p.registrations} fmt={fmtInt} />{showRatio && <RatioSpan num={p.registrations} den={p.clicks} {...RATIO_CLICKS_REG} />}</> },
    { key: 'ftd', label: 'FTD', numeric: true, render: (_, p) => <><Muted v={p.ftd} fmt={fmtInt} />{showRatio && <RatioSpan num={p.ftd} den={p.registrations} {...RATIO_REG_FTD} />}</> },
    { key: 'depositsCount', label: 'Vklady (#)', numeric: true, render: (_, p) => <Muted v={p.depositsCount} fmt={fmtInt} /> },
    { key: 'depositsVolume', label: 'Vklady', numeric: true, render: (_, p) => <Muted v={p.depositsVolume} fmt={(v) => fmtMoney(v, currency, eurRate)} /> },
    { key: 'cpa', label: 'CPA', numeric: true, render: (_, p) => <Muted v={p.cpa} fmt={(v) => fmtMoney(v, currency, eurRate)} /> },
    { key: 'rs', label: 'RS', numeric: true, render: (_, p) => <Muted v={p.rs} fmt={(v) => fmtMoney(v, currency, eurRate)} /> },
    { key: 'detail', label: 'Detail', width: 105, sortable: false, render: (_, p) => <Button variant="ghost" size="sm" onClick={() => onDrill(p.campaignId, p.campaignName)}>Zobrazit</Button> },
  ];
  return <div className="rondo-arc-affiliates-table"><SortableDataTable rows={tableRows} columns={columns} rowKey="campaignId" caption="Výkon affiliate partnerů" emptyMessage="Žádná data pro aktuální měsíc." /></div>;
}

function PartnerCell({ campaignId, campaignName }: { campaignId: string; campaignName: string }) {
  const color = avatarColor(campaignName || campaignId);
  return (
    <div className="finance-affiliate-partner">
      <div style={{ background: color }}>
        {initials(campaignName || campaignId)}
      </div>
      <strong title={campaignId}>{campaignName || campaignId}</strong>
    </div>
  );
}

function Muted({ v, fmt }: { v: number; fmt: (v: number) => string }) {
  const s = fmt(v);
  const zero = (Number(v) || 0) === 0;
  return zero ? <span className="text-muted">{s}</span> : <>{s}</>;
}

function fmtPct(p: number): string {
  return `${p.toLocaleString('cs-CZ', { maximumFractionDigits: p >= 10 ? 0 : 1 })} %`;
}
function pctColor(p: number, good: number, bad: number): string {
  if (p >= good) return '#137333';
  if (p <= bad) return '#c2410c';
  return '#9ca3af';
}
function RatioSpan({ num, den, good, bad }: { num: number; den: number; good: number; bad: number }) {
  if (!(den > 0)) return null;
  const p = (num / den) * 100;
  return (
    <span style={{ fontSize: '0.72rem', marginLeft: 4, fontWeight: 500, color: pctColor(p, good, bad) }}>
      ({fmtPct(p)})
    </span>
  );
}
// Konverzní prahy (%, vyšší = lepší). Registrace = reg/prokliky: ≥5 dobré, ≤2
// slabé. FTD = ftd/registrace: ≥40 dobré, ≤20 slabé.
const RATIO_CLICKS_REG = { good: 5, bad: 2 };
const RATIO_REG_FTD = { good: 40, bad: 20 };

const METRIC_LABELS: Record<ChartMetric, string> = {
  clicks: 'Prokliky',
  registrations: 'Registrace',
  ftd_count: 'FTD',
  deposits_count: 'Vklady (#)',
  deposits_volume: 'Vklady',
  ngr_volume: 'NGR',
  commission: 'Provize',
};
const METRIC_KPI_ICON: Record<ChartMetric, { icon: string; bg: string; fg: string }> = {
  clicks:          { icon: 'mouse-pointer',  bg: '#eef2ff', fg: '#4338ca' },
  registrations:   { icon: 'user-plus',     bg: '#eff6ff', fg: '#1d4ed8' },
  ftd_count:       { icon: 'star',          bg: '#f3e8ff', fg: '#7c3aed' },
  deposits_count:  { icon: 'package',       bg: '#fff7ed', fg: '#c2410c' },
  deposits_volume: { icon: 'dollar-sign',   bg: '#e7f7ed', fg: '#137333' },
  ngr_volume:      { icon: 'trending-up',   bg: '#fef3c7', fg: '#92400e' },
  commission:      { icon: 'award',         bg: '#fee2e2', fg: '#b91c1c' },
};

const METRIC_IS_MONEY = (m: ChartMetric): boolean =>
  m === 'deposits_volume' || m === 'ngr_volume' || m === 'commission';

function metricValue(p: CampaignChartResult['points'][number], m: ChartMetric): number {
  switch (m) {
    case 'clicks': return p.clicks;
    case 'registrations': return p.registrations;
    case 'ftd_count': return p.ftdCount;
    case 'deposits_count': return p.depositsCount;
    case 'deposits_volume': return p.depositsVolume;
    case 'ngr_volume': return p.ngrVolume;
    case 'commission': return p.commission;
  }
}

function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

function CampaignDrillModal({
  campaignId, campaignName, onClose,
}: { campaignId: string; campaignName: string; onClose: () => void }) {
  const [metrics, setMetrics] = useState<ChartMetric[]>(['clicks', 'registrations']);
  const toggleMetric = (k: ChartMetric) => {
    setMetrics((cur) => {
      if (cur.includes(k)) return cur.length === 1 ? cur : cur.filter((m) => m !== k);
      return cur.length >= 2 ? cur : [...cur, k];
    });
  };
  const [range, setRange] = useState<ChartRange>('6');
  const [granularity, setGranularity] = useState<ChartGranularity>('day');
  const [data, setData] = useState<CampaignChartResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAffiliatesCampaignChart({ campaignId, range, granularity })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || 'Chyba'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaignId, range, granularity]);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const labels = data.points.map((p) => {
      const d = new Date(p.date);
      if (granularity === 'month') return `${MONTHS_CS[d.getMonth()]?.slice(0, 3)} ${String(d.getFullYear()).slice(2)}`;
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
    });
    const m0 = metrics[0] as ChartMetric;
    const m1 = metrics[1];
    const fmtAxis = (m: ChartMetric) => (v: number | string) =>
      METRIC_IS_MONEY(m) ? `${fmtInt(Number(v))} €` : fmtInt(Number(v));

    const datasets: Record<string, unknown>[] = [
      {
        type: 'bar',
        label: METRIC_LABELS[m0],
        data: data.points.map((p) => metricValue(p, m0)),
        backgroundColor: hexA(METRIC_KPI_ICON[m0].fg, 0.65),
        borderColor: METRIC_KPI_ICON[m0].fg,
        borderWidth: 1,
        yAxisID: 'y',
        order: 2,
      },
    ];
    if (m1) {
      datasets.push({
        type: 'line',
        label: METRIC_LABELS[m1],
        data: data.points.map((p) => metricValue(p, m1)),
        borderColor: METRIC_KPI_ICON[m1].fg,
        backgroundColor: METRIC_KPI_ICON[m1].fg,
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.3,
        yAxisID: 'y1',
        order: 1,
      });
    }

    const chart = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets: datasets as never },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: !!m1, position: 'top' },
          tooltip: {
            callbacks: {
              label: (c) => {
                const m = (c.datasetIndex === 1 && m1 ? m1 : m0);
                const v = Number(c.parsed.y) || 0;
                return `${METRIC_LABELS[m]}: ${METRIC_IS_MONEY(m) ? `${fmtInt(v)} €` : fmtInt(v)}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            title: { display: true, text: METRIC_LABELS[m0] },
            ticks: { callback: fmtAxis(m0) },
          },
          ...(m1
            ? {
                y1: {
                  beginAtZero: true,
                  position: 'right',
                  grid: { drawOnChartArea: false },
                  title: { display: true, text: METRIC_LABELS[m1] },
                  ticks: { callback: fmtAxis(m1) },
                },
              }
            : {}),
        },
      },
    });
    chartRef.current = chart;
    return () => { chart.destroy(); chartRef.current = null; };
  }, [data, metrics, granularity]);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={campaignName} description="Aktuální měsíc a historie výkonu" className="rondo-arc-affiliates-dialog p1-arc-scope">
        {data ? <div className="rondo-arc-affiliates-modal-kpis">
          {(Object.keys(METRIC_LABELS) as ChartMetric[]).map((key) => {
            const raw = metricValue(data.mtd as CampaignChartResult['points'][number], key);
            const ratio = key === 'registrations' ? data.mtd.clicks ? ` · ${fmtPct((data.mtd.registrations / data.mtd.clicks) * 100)} z prokliků` : ''
              : key === 'ftd_count' ? data.mtd.registrations ? ` · ${fmtPct((data.mtd.ftdCount / data.mtd.registrations) * 100)} z registrací` : '' : '';
            return <MetricCard key={key} label={METRIC_LABELS[key]} value={raw} suffix={METRIC_IS_MONEY(key) ? '\u00a0€' : undefined} context={`Aktuální měsíc${ratio}`} />;
          })}
        </div> : <PageState title="Načítám detail kampaně" loading />}
        <div className="rondo-arc-affiliates-modal-controls">
          <div className="rondo-arc-affiliates-metrics" role="group" aria-label="Ukazatele grafu">
            {(Object.keys(METRIC_LABELS) as ChartMetric[]).map((key) => {
              const active = metrics.includes(key);
              const disabled = !active && metrics.length >= 2;
              return <Button key={key} type="button" size="sm" variant={active ? 'primary' : 'secondary'} disabled={disabled} aria-pressed={active} onClick={() => toggleMetric(key)}>{METRIC_LABELS[key]}</Button>;
            })}
          </div>
          <div className="rondo-arc-affiliates-modal-selects">
            <CompactSelect value={granularity} onChange={(v) => setGranularity(v as ChartGranularity)} ariaLabel="Granularita grafu" options={[{ value: 'day', label: 'Dny' }, { value: 'month', label: 'Měsíce' }]} />
            <CompactSelect value={range} onChange={(v) => setRange(v as ChartRange)} ariaLabel="Období grafu" options={[{ value: '3', label: '3 měsíce' }, { value: '6', label: '6 měsíců' }, { value: '12', label: '12 měsíců' }, { value: 'all', label: 'Vše' }]} />
          </div>
        </div>
        {error ? <PageState title="Graf se nepodařilo načíst" message={error} /> : <Card title="Vývoj výkonu" description="Zvolte jeden nebo dva ukazatele" className="rondo-arc-affiliates-chart">
          {loading && <p>Načítám graf…</p>}
          <div className="rondo-arc-affiliates-canvas"><canvas ref={canvasRef} /></div>
        </Card>}
      </DialogContent>
    </Dialog>
  );
}
